/**
 * CoreAuth Utility Functions
 * 
 * Business logic extracted from reducer to maintain pure reducer functions.
 * These utilities handle complex calculations related to promotions and bonuses.
 */

import * as Big from 'big.js';
import { PromotionPeriods } from './coreAuth.models';

interface PlayerBonus {
  end_date: string;
  state: 'eligible' | 'paid' | 'opted_in';
  spend_amount?: string | number;
  thresholds: Array<{
    currency: string;
    min_amount: string | number;
  }>;
}

/**
 * Memoized helper function for date comparison
 * Checks if a date is within 12 hours of expiration
 */
const memoizedCheckAlmostCompleted = (() => {
  const cache = new Map<string, boolean>();
  const TWELVE_HOURS_MS = 1000 * 12 * 60 * 60;

  return (expire_date: Date | string): boolean => {
    const key = expire_date.toString();
    if (cache.has(key)) {
      return cache.get(key)!;
    }

    const now = Date.now();
    const expiry = new Date(expire_date).getTime();
    const result = (expiry - now) <= TWELVE_HOURS_MS;

    cache.set(key, result);
    return result;
  };
})();

/**
 * Parse New Player Bonus to determine promotion period status
 * 
 * @param new_player_bonus - Player bonus data from backend
 * @param currency_code - User's currency code
 * @returns PromotionPeriods enum value indicating bonus status
 */
export const parseNewPlayerBonus = (
  new_player_bonus: PlayerBonus | undefined,
  currency_code: string | undefined
): PromotionPeriods | undefined => {
  if (!new_player_bonus || !currency_code) {
    return PromotionPeriods.NotExistent;
  }

  const expire_date = new Date(new_player_bonus.end_date);
  const current_date = new Date();

  const isBonusPeriodExpired = expire_date.getTime()
    ? current_date.getTime() >= expire_date.getTime()
    : false;

  if (isBonusPeriodExpired) {
    return PromotionPeriods.Expired;
  }

  if (new_player_bonus.state === 'eligible') {
    return PromotionPeriods.Eligible;
  }

  if (new_player_bonus.state === 'paid') {
    return PromotionPeriods.Completed;
  }

  if (new_player_bonus.state === 'opted_in') {
    const threshold = new_player_bonus.thresholds
      .find(t => t.currency === currency_code);

    if (!threshold) {
      return PromotionPeriods.NotExistent;
    }

    const spendAmount = new Big.Big(new_player_bonus.spend_amount || 0);
    const minAmount = new Big.Big(threshold.min_amount);
    const isCompleted = spendAmount.gte(minAmount);

    if (isCompleted) {
      return PromotionPeriods.Completed;
    }

    return memoizedCheckAlmostCompleted(expire_date)
      ? PromotionPeriods.Ending
      : PromotionPeriods.Begun;
  }

  return undefined;
};

/**
 * Helper for creating optional data
 */
export const createOptionalData = (data: Partial<any>): any => ({
  ...(data.user_preferences && { user_preferences: data.user_preferences }),
  ...(data.registered_at && { registered_at: data.registered_at }),
  ...(data.wallet && { wallet: data.wallet }),
});

/**
 * Helper for creating auth token state
 */
export const createAuthToken = (authorization_code: string | undefined, tokens: any) => ({
  authorization_code,
  tokens
});

/**
 * Helper for creating session optional data
 */
export const createSessionOptionalData = (data: any, currency_code: string | undefined) => ({
  ...(data.additional_info?.age_verified && { age_verified: data.additional_info.age_verified }),
  ...(data.free_play_session?.new_player_bonus && { new_player_bonus: data.free_play_session.new_player_bonus }),
  period_status: parseNewPlayerBonus(data.free_play_session?.new_player_bonus, currency_code)
});
